import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Alert,
  Divider,
  Grid,
  Paper,
  Chip
} from '@mui/material';
import { Save as SaveIcon, Notifications as NotificationsIcon } from '@mui/icons-material';
import api from '../utils/api';

interface NotificationPreferences {
  id?: number;
  email_enabled: boolean;
  email_assignment_notifications: boolean;
  email_overdue_reminders: boolean;
  email_due_soon_warnings: boolean;
  email_recertification_reminders: boolean;
  email_manager_alerts: boolean;
  email_completion_summaries: boolean;
  email_system_notifications: boolean;
  due_soon_days: number;
  overdue_reminder_frequency: number;
}

const NotificationSettings: React.FC = () => {
  const [preferences, setPreferences] = useState<NotificationPreferences>({
    email_enabled: true,
    email_assignment_notifications: true,
    email_overdue_reminders: true,
    email_due_soon_warnings: true,
    email_recertification_reminders: true,
    email_manager_alerts: true,
    email_completion_summaries: true,
    email_system_notifications: true,
    due_soon_days: 7,
    overdue_reminder_frequency: 1
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      setLoading(true);
      const response = await api.get('/notification-preferences/');
      setPreferences(response.data);
    } catch (error) {
      console.error('Error fetching notification preferences:', error);
      setError('Failed to load notification preferences');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      
      if (preferences.id) {
        await api.put(`/notification-preferences/${preferences.id}/`, preferences);
      } else {
        await api.post('/notification-preferences/', preferences);
      }
      
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving notification preferences:', error);
      setError('Failed to save notification preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (field: keyof NotificationPreferences) => {
    setPreferences(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleNumberChange = (field: keyof NotificationPreferences, value: number) => {
    setPreferences(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const dueSoonDaysEnabled =
    preferences.email_enabled &&
    (preferences.email_due_soon_warnings || preferences.email_recertification_reminders);

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading notification preferences...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <NotificationsIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h4" component="h1">
          Notification Settings
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(false)}>
          Notification preferences saved successfully!
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 3 }}>
        Assignment emails are combined into one weekday digest at 7:30 AM in your local timezone.
        Sections below control what is included. No email is sent when there is nothing to report.
      </Alert>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardHeader 
              title="Daily Digest Email" 
              subheader="Choose which sections appear in your weekday morning digest"
            />
            <CardContent>
              <FormControlLabel
                control={
                  <Switch
                    checked={preferences.email_enabled}
                    onChange={() => handleToggle('email_enabled')}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" fontWeight="medium">
                      Enable Email Notifications
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Master switch for all email notifications
                    </Typography>
                  </Box>
                }
              />
              
              <Divider sx={{ my: 2 }} />
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.email_assignment_notifications}
                      onChange={() => handleToggle('email_assignment_notifications')}
                      disabled={!preferences.email_enabled}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1">New Assignments</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Include assignments newly assigned to you since the previous digest
                      </Typography>
                    </Box>
                  }
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.email_due_soon_warnings}
                      onChange={() => handleToggle('email_due_soon_warnings')}
                      disabled={!preferences.email_enabled}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1">Coming Due</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Include assignments due within your due-soon window
                      </Typography>
                    </Box>
                  }
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.email_overdue_reminders}
                      onChange={() => handleToggle('email_overdue_reminders')}
                      disabled={!preferences.email_enabled}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1">Overdue</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Include overdue assignments every weekday
                      </Typography>
                    </Box>
                  }
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.email_recertification_reminders}
                      onChange={() => handleToggle('email_recertification_reminders')}
                      disabled={!preferences.email_enabled}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1">Certifications</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Include certifications coming due within your due-soon window and any that are expired
                      </Typography>
                    </Box>
                  }
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.email_completion_summaries}
                      onChange={() => handleToggle('email_completion_summaries')}
                      disabled={!preferences.email_enabled}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1">Completions</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Include assignments you completed since the previous digest
                      </Typography>
                    </Box>
                  }
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.email_manager_alerts}
                      onChange={() => handleToggle('email_manager_alerts')}
                      disabled={!preferences.email_enabled}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1">Team Digest (Managers & Admins)</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Separate weekday email with team completions and expired certifications
                      </Typography>
                    </Box>
                  }
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.email_system_notifications}
                      onChange={() => handleToggle('email_system_notifications')}
                      disabled={!preferences.email_enabled}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1">System Notifications</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Get notified about system updates and maintenance
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardHeader 
              title="Timing Settings" 
              subheader="Configure how far ahead coming-due items appear in your digest"
            />
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Due Soon Days"
                    type="number"
                    value={preferences.due_soon_days}
                    onChange={(e) => handleNumberChange('due_soon_days', parseInt(e.target.value) || 7)}
                    disabled={!dueSoonDaysEnabled}
                    helperText="Days ahead to include coming-due assignments and certifications"
                    fullWidth
                    inputProps={{ min: 1, max: 30 }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography variant="h6" gutterBottom>
              Current Settings Summary
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Chip 
                label={`Email: ${preferences.email_enabled ? 'Enabled' : 'Disabled'}`}
                color={preferences.email_enabled ? 'success' : 'default'}
                size="small"
              />
              <Chip 
                label={`New: ${preferences.email_assignment_notifications ? 'On' : 'Off'}`}
                color={preferences.email_assignment_notifications ? 'primary' : 'default'}
                size="small"
              />
              <Chip 
                label={`Coming Due: ${preferences.email_due_soon_warnings ? 'On' : 'Off'}`}
                color={preferences.email_due_soon_warnings ? 'primary' : 'default'}
                size="small"
              />
              <Chip 
                label={`Overdue: ${preferences.email_overdue_reminders ? 'On' : 'Off'}`}
                color={preferences.email_overdue_reminders ? 'primary' : 'default'}
                size="small"
              />
              <Chip 
                label={`Certifications: ${preferences.email_recertification_reminders ? 'On' : 'Off'}`}
                color={preferences.email_recertification_reminders ? 'primary' : 'default'}
                size="small"
              />
              <Chip 
                label={`Completions: ${preferences.email_completion_summaries ? 'On' : 'Off'}`}
                color={preferences.email_completion_summaries ? 'primary' : 'default'}
                size="small"
              />
              <Chip 
                label={`Team Digest: ${preferences.email_manager_alerts ? 'On' : 'Off'}`}
                color={preferences.email_manager_alerts ? 'primary' : 'default'}
                size="small"
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving}
          size="large"
        >
          {saving ? 'Saving...' : 'Save Preferences'}
        </Button>
      </Box>
    </Box>
  );
};

export default NotificationSettings;
